/**
 * DESCRIPTION: Edit component for the Event Date block. Reads event date/time
 * from post meta via context and displays formatted output.
 */
import { __ } from '@wordpress/i18n';
import { useBlockProps, InspectorControls } from '@wordpress/block-editor';
import {
	PanelBody,
	ToggleControl,
	TextControl,
	Spinner,
} from '@wordpress/components';
import { useSelect } from '@wordpress/data';

export default function Edit( { attributes, setAttributes, context } ) {
	const { format, showTime, showIcon } = attributes;
	const { postId, postType } = context;

	const blockProps = useBlockProps( {
		className: 'nemtly-event-date',
	} );

	const { eventDate, eventTime, eventType, isLoading } = useSelect(
		( select ) => {
			if ( ! postId ) {
				return {
					eventDate: null,
					eventTime: null,
					eventType: null,
					isLoading: false,
				};
			}

			const { getEditedEntityRecord, hasFinishedResolution } =
				select( 'core' );
			const record = getEditedEntityRecord(
				'postType',
				postType || 'nemtly_event',
				postId
			);

			const finished = hasFinishedResolution( 'getEditedEntityRecord', [
				'postType',
				postType || 'nemtly_event',
				postId,
			] );

			const meta = record?.meta || {};

			return {
				eventDate: meta.nemtly_event_date || null,
				eventTime: meta.nemtly_event_time || null,
				eventType: meta.nemtly_event_type || null,
				isLoading: ! finished,
			};
		},
		[ postId, postType ]
	);

	// Slot-based events don't have a single event date.
	if ( eventType === 'one-on-one' || eventType === 'group' ) {
		return null;
	}

	/**
	 * Format a date string for display.
	 *
	 * @param {string} dateStr Date in YYYY-MM-DD format.
	 * @param {string} timeStr Time in HH:MM format (24h).
	 *
	 * @return {Object} Formatted date and time strings.
	 */
	const formatEventDate = ( dateStr, timeStr ) => {
		if ( ! dateStr ) {
			return { formattedDate: null, formattedTime: null };
		}

		try {
			const dateObj = new Date( dateStr + 'T' + ( timeStr || '00:00' ) );

			if ( isNaN( dateObj.getTime() ) ) {
				return { formattedDate: dateStr, formattedTime: timeStr };
			}

			const formattedDate = dateObj.toLocaleDateString( undefined, {
				year: 'numeric',
				month: 'short',
				day: 'numeric',
			} );

			let formattedTime = null;
			if ( timeStr ) {
				formattedTime = dateObj.toLocaleTimeString( undefined, {
					hour: 'numeric',
					minute: '2-digit',
					hour12: true,
				} );
			}

			return { formattedDate, formattedTime };
		} catch ( error ) {
			return { formattedDate: dateStr, formattedTime: timeStr };
		}
	};

	const { formattedDate, formattedTime } = formatEventDate(
		eventDate,
		eventTime
	);

	// Show placeholder when no postId (e.g., block inserted outside query loop context).
	const displayDate = formattedDate || __( 'Event Date', 'nemtly-booking' );
	const displayTime = formattedTime || __( '12:00 PM', 'nemtly-booking' );

	return (
		<>
			<InspectorControls>
				<PanelBody title={ __( 'Settings', 'nemtly-booking' ) }>
					<TextControl
						label={ __( 'Date Format', 'nemtly-booking' ) }
						value={ format }
						onChange={ ( value ) =>
							setAttributes( { format: value } )
						}
						help={ __(
							'Enter a PHP date format string (e.g. F j, Y). Leave empty for system default.',
							'nemtly-booking'
						) }
					/>
					<ToggleControl
						label={ __( 'Show Time', 'nemtly-booking' ) }
						checked={ showTime }
						onChange={ ( value ) =>
							setAttributes( { showTime: value } )
						}
					/>
					<ToggleControl
						label={ __( 'Show Icon', 'nemtly-booking' ) }
						checked={ showIcon }
						onChange={ ( value ) =>
							setAttributes( { showIcon: value } )
						}
					/>
				</PanelBody>
			</InspectorControls>

			<div { ...blockProps }>
				{ isLoading && <Spinner /> }
				{ ! isLoading && (
					<>
						{ showIcon && (
							<svg
								width="20"
								height="20"
								className="nb-mr-2"
								fill="currentColor"
								viewBox="0 0 20 20"
							>
								<path
									fillRule="evenodd"
									d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
									clipRule="evenodd"
								/>
							</svg>
						) }
						<span className="nemtly-date-preview">
							{ displayDate }
						</span>
						{ showTime && (
							<>
								<span className="nemtly-event-time-separator">
									{ ' | ' }
								</span>
								<span className="nemtly-time-preview">
									{ displayTime }
								</span>
							</>
						) }
					</>
				) }
			</div>
		</>
	);
}
